import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { CalendarDays, CheckCircle, Users, Clock, Mail } from 'lucide-react'
import { reservationsAPI } from '../../services/api'
import { useTenant } from '../../contexts/TenantContext'
import AvailabilityCalendar from '../../components/AvailabilityCalendar'
import TimeSlotPicker from '../../components/TimeSlotPicker'
import { t, locale } from '../../i18n'

export default function ReservationPage() {
  const tenant = useTenant()
  const location = useLocation()
  const [date, setDate] = useState(null)
  const [party, setParty] = useState(2)
  const [grid, setGrid] = useState([])
  const [slot, setSlot] = useState('')
  const [zone, setZone] = useState('')
  const [noTables, setNoTables] = useState(false)
  const [closedReason, setClosedReason] = useState('')
  const [calendarKey, setCalendarKey] = useState(0)
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState(null)
  const [form, setForm] = useState({ customerName: '', customerPhone: '', customerEmail: '', notes: '' })

  useEffect(() => {
    if (!date) return
    let current = true
    setSlot(''); setGrid([]); setError(''); setClosedReason(''); setLoading(true)
    reservationsAPI.availability(date, party)
      .then(({ data }) => {
        if (!current) return
        setGrid(data.grid || (data.slots || []).map(time => ({ time, status: 'available' })))
        setZone(data.timeZone)
        setNoTables(Boolean(data.noTables))
        if (data.closed) setClosedReason(data.reason || 'Closed')
      })
      .catch(e => { if (current) setError(t(e.response?.data?.error || 'Could not load times')) })
      .finally(() => { if (current) setLoading(false) })
    return () => { current = false }
  }, [date, party])

  const submit = async e => {
    e.preventDefault()
    if (!slot || busy) return
    setBusy(true); setError('')
    try {
      const { data } = await reservationsAPI.create({ ...form, date, partySize: party, startTime: slot })
      setResult(data.reservation)
    } catch (e) {
      setError(t(e.response?.data?.error || 'Could not reserve this time'))
      if (e.response?.status === 409) {
        setGrid(g => g.map(s => (s.time === slot ? { ...s, status: 'booked' } : s)))
        setCalendarKey(k => k + 1)
      }
      setSlot('')
    } finally { setBusy(false) }
  }

  if (!(tenant.services || []).includes('menu') || tenant.settings?.reservations_enabled === false) {
    return (
      <section className="flow-panel max-w-xl mx-auto text-center">
        <CalendarDays className="mx-auto h-12 w-12 mb-4 opacity-60" />
        <h1 className="flow-title">{t("Online reservations are closed")}</h1>
        <p className="mt-4 text-slate-600">{tenant.name} {t("is not taking table reservations online right now. Please contact the restaurant directly.")}</p>
        <Link to={`/${location.search}`} className="flow-primary mt-6 inline-block">{t("Back to the menu")}</Link>
      </section>
    )
  }

  if (result) {
    return (
      <section className="flow-panel max-w-xl mx-auto text-center">
        <CheckCircle className="mx-auto h-14 w-14 text-emerald-700 mb-5" />
        <p className="eyebrow">{t("REQUEST RECEIVED")}</p>
        <h1 className="flow-title">{t("A seat at our table.")}</h1>
        <p className="mt-5 text-slate-600">
          {t("Reservation #")}{result.id} {t("for")} {party} {t("guests on")} {new Date(result.start_time).toLocaleString(locale(), { timeZone: zone || undefined })}.{' '}
          {tenant.name} {t("will contact you to confirm")}{result.customer_email ? t(" - we'll also email {customer_email}", { customer_email: result.customer_email }) : ''}.
        </p>
        <button className="flow-primary mt-6" onClick={() => { setResult(null); setSlot(''); setCalendarKey(k => k + 1) }}>{t("Make another reservation")}</button>
      </section>
    )
  }

  const emptyMessage = noTables
    ? 'No table fits a party this size. Choose fewer guests or call us.'
    : closedReason
      ? `Closed this day${closedReason !== 'Closed' ? ` - ${closedReason}` : ''}. Choose another date.`
      : 'No tables left this day. Choose another date.'

  return (
    <div className="commerce-flow grid lg:grid-cols-[1fr_1.3fr] gap-10 items-start">
      <section className="reservation-intro">
        <p className="eyebrow">{t("YOU'RE INVITED")}</p>
        <h1 className="flow-title">{t("Good company.")}<br />{t("A table for you.")}</h1>
        <p className="text-slate-600 text-lg mt-6 leading-relaxed">{t("Plan your next visit to")} {tenant.name || t("our restaurant")}{t(". Pick your party size, a green day, and a time. We will take care of the table.")}</p>
        <div className="space-y-5 mt-10">
          <p className="flex gap-3"><Users /> {t("Parties of 1–30 guests")}</p>
          <p className="flex gap-3"><Clock /> {t("Your table is reserved for 90 minutes")}</p>
          <p className="flex gap-3"><CalendarDays /> {t("Times shown in")} {zone || t("the restaurant time zone")}</p>
          <p className="flex gap-3"><Mail /> {t("Add your email to get the confirmation")}</p>
        </div>
      </section>

      <form className="flow-panel" onSubmit={submit}>
        <h2 className="text-2xl font-semibold mb-6">{t("Reserve a table")}</h2>

        <label htmlFor="reservation-guests" className="flow-label">{t("Guests")}</label>
        <select id="reservation-guests" className="flow-input mb-5" value={party} onChange={e => setParty(Number(e.target.value))}>
          {Array.from({ length: 30 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1} {i === 0 ? t("guest") : t("guests")}</option>)}
        </select>

        <p className="flow-label">{t("Choose a day")}</p>
        <AvailabilityCalendar
          loadMonth={month => reservationsAPI.calendar(month, party).then(r => r.data)}
          value={date}
          onChange={d => { setDate(d); setSlot('') }}
          reloadKey={`${party}-${calendarKey}`}
        />

        {date && (
          <fieldset className="mt-6">
            <legend className="flow-label">
              {t("Choose a time ·")} {new Date(`${date}T12:00:00`).toLocaleDateString(locale(), { weekday: 'long', day: 'numeric', month: 'long' })}
            </legend>
            <TimeSlotPicker slots={grid} value={slot} onChange={setSlot} timeZone={zone} loading={loading} emptyMessage={emptyMessage} />
          </fieldset>
        )}

        {slot && (
          <div className="mt-6 border-t pt-6">
            <label htmlFor="reservation-name" className="flow-label">{t("Your name")}</label>
            <input id="reservation-name" className="flow-input" autoComplete="name" required maxLength={100} value={form.customerName} onChange={e => setForm({ ...form, customerName: e.target.value })} />
            <label htmlFor="reservation-phone" className="flow-label mt-4">{t("Phone number")}</label>
            <input id="reservation-phone" className="flow-input" autoComplete="tel" type="tel" required maxLength={30} value={form.customerPhone} onChange={e => setForm({ ...form, customerPhone: e.target.value })} />
            <label htmlFor="reservation-email" className="flow-label mt-4">{t("Email (optional - for your confirmation)")}</label>
            <input id="reservation-email" className="flow-input" autoComplete="email" type="email" maxLength={150} value={form.customerEmail} onChange={e => setForm({ ...form, customerEmail: e.target.value })} />
            <label htmlFor="reservation-notes" className="flow-label mt-4">{t("Anything we should know? (optional)")}</label>
            <textarea id="reservation-notes" className="flow-input" rows={2} maxLength={1000} placeholder={t("Accessibility needs, a special occasion…")} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
          </div>
        )}

        {error && <p role="alert" className="text-red-700 mt-4">{error}</p>}
        <button disabled={!slot || busy || loading} className="flow-primary w-full mt-6">{busy ? t("Requesting your table…") : t("Request reservation")}</button>
        <p className="text-xs text-center text-slate-500 mt-3">{t("Your reservation is pending until the restaurant confirms it.")}</p>
      </form>
    </div>
  )
}
